import { graphql } from 'react-relay';
import { useEffect, useMemo } from 'react';
import { isStatusCodeError } from '@confluence/error-types/entry-points/isStatusCodeError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import type {
	useTrackTitleRedactionRenderedRedactionsCountQuery as useTrackTitleRedactionRenderedRedactionsCountQuery$data,
	useTrackTitleRedactionRenderedRedactionsCountQueryVariables as useTrackTitleRedactionRenderedRedactionsCountQuery$variables,
} from './__types__/useTrackTitleRedactionRenderedRedactionsCountQuery.ts';
import { useRedactionsTourActions } from './useRedactionsTourState.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
export const TITLE_REDACTION_STRING = '████';

export const doesTitleContainRedactionString = (title?: string) => {
	if (!title) {
		return false;
	}
	return title.includes(TITLE_REDACTION_STRING);
};

type UseTrackTitleRedactionRenderedProps = {
	contentId: string;
	title?: string;
};

export const useTrackTitleRedactionRendered = ({
	contentId,
	title,
}: UseTrackTitleRedactionRenderedProps) => {
	const { setTitleRedactionRenderedForContentId } = useRedactionsTourActions();

	const titleMaybeHasRedaction = useMemo(() => doesTitleContainRedactionString(title), [title]);
	const { data, error: redactionsCountError } = useQueryInterop<
		useTrackTitleRedactionRenderedRedactionsCountQuery$data,
		useTrackTitleRedactionRenderedRedactionsCountQuery$variables
	>(
		graphql`
			query useTrackTitleRedactionRenderedRedactionsCountQuery($contentId: ID!) {
				singleContent(id: $contentId) {
					id
					redactionMetadataCount
				}
			}
		`,
		{
			skip: !titleMaybeHasRedaction,
			variables: {
				contentId,
			},
			errorPolicy: 'all',
		},
	);

	// handle 4xx errors
	useEffect(() => {
		if (redactionsCountError && isStatusCodeError(redactionsCountError, '404')) {
			markErrorAsHandled(redactionsCountError);
		}
	}, [redactionsCountError]);

	const redactionsCount = data?.singleContent?.redactionMetadataCount ?? 0;

	useEffect(() => {
		if (contentId && titleMaybeHasRedaction && redactionsCount > 0) {
			setTitleRedactionRenderedForContentId(contentId);
		}
		return () => {
			// Cleanup function to reset the title redaction state
			setTitleRedactionRenderedForContentId(null);
		};
	}, [contentId, titleMaybeHasRedaction, redactionsCount, setTitleRedactionRenderedForContentId]);
};
